import { CommandBar, useTheme } from '@fluentui/react';
import React, { useEffect, useState } from "react";
import { batch } from 'react-redux';
import { useMediaQuery } from 'usehooks-ts';
import { XDataTable, XTextField, XTreeView } from '../components';
import { IoipIcons } from '../components/IoipIcons';
import { Paginator } from '../components/Paginator';
import { DataTableRequest } from '../data';
import { useLayout, useType } from '../hooks';
import { useLanguage } from '../languages';
import { Page } from '../surfaces';
import { defaultIDataTableRequest } from '../types';
import { TreeNode } from '../types/TreeNode';
import { useCommandBar } from './ACCommandBar';
import { useSearch } from './Header';
export const ACDataTablePage = function ACDataTablePage(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const lang = useLanguage();
    const commandBar = useCommandBar();
    const isSidebarVisible = useMediaQuery('(min-width:540px)');
    const theme = useTheme();
    const layout = useLayout();
    const search = useSearch();
    const [foldersNode, setFoldersNode] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [commandBarFarItems, setCommandBarFarItems] = useState(props.commandBarFarItems || []);
    const [request, setRequest] = useType(defaultIDataTableRequest());
    const [resp, setResponseStatus] = useState({
        totalPages: 0,
        lastItem: 0,
        totalRecords: 0,
        items: []
    });
    useEffect(function onFoldersChanged() {
        const nodes = [
            {
                id: '__root__',
                text: props.rootNodeText || 'Root',
                icon: IoipIcons.Folder,
            }
        ];
        const folders = props.folders || [];
        for (let i = 0; i < folders.length; i++) {
            const folder = folders[i];
            const parts = folder.split('/');
            parts.reduce((parentPath, part) => {
                const fullPath = `${parentPath}/${part}`;
                const node = nodes.find(x => x.id === fullPath);
                if (node == null) {
                    nodes.push({
                        id: fullPath,
                        text: part,
                        icon: IoipIcons.Folder,
                        parentId: parentPath,
                        onClick: () => {
                            var _a;
                            (_a = props.onFolderSelected) === null || _a === void 0 ? void 0 : _a.call(props, fullPath.replace('__root__/', ''));
                        }
                    });
                }
                return fullPath;
            }, '__root__');
        }
        setFoldersNode(TreeNode.createRootNode(nodes, '__root__'));
    }, [props.folders]);
    useEffect(function onCommandBarFarItemsChanged() {
        var _a;
        const items = [];
        if (((_a = props.commandBarFarItems) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            for (let i = 0; i < props.commandBarFarItems.length; i++) {
                items.push(props.commandBarFarItems[i]);
            }
        }
        const onChangePaginationLength = (value) => {
            setRequest(Object.assign(Object.assign({}, request), { length: value, start: 1 }));
        };
        const length = request.length || 20;
        items.push({
            key: length.toString(),
            iconProps: { iconName: 'NumberedList' },
            name: length.toString(),
            subMenuProps: {
                items: [
                    {
                        key: '10',
                        name: '10',
                        onClick: () => onChangePaginationLength(10)
                    },
                    {
                        key: '15',
                        name: '15',
                        onClick: () => onChangePaginationLength(15)
                    },
                    {
                        key: '20',
                        name: '20',
                        onClick: () => onChangePaginationLength(20)
                    },
                    {
                        key: '30',
                        name: '30',
                        onClick: () => onChangePaginationLength(30)
                    },
                    {
                        key: '50',
                        name: '50',
                        onClick: () => onChangePaginationLength(50)
                    }
                ]
            }
        });
        if (props.useSiteCommandBar) {
            commandBar.setFarItems(items);
        }
        else
            setCommandBarFarItems(items);
    }, [props.commandBarFarItems, request]);
    useEffect(function onRequestChanged() {
        downloadServerDataAsync();
    }, [request]);
    useEffect(function componentDidMount() {
        var _a;
        (_a = props.refreshRef) === null || _a === void 0 ? void 0 : _a.call(props, () => downloadServerDataAsync());
        if (props.useSiteCommandBar) {
            batch(() => {
                layout.showSearchBox('');
                search.subscribe((value) => setRequest({ search: value }));
            });
        }
        return function componentWillUnmount() {
            if (props.useSiteCommandBar) {
                batch(() => {
                    commandBar.clearItems();
                    commandBar.clearFarItems();
                    search.unsubscribe();
                    layout.resetSearchBox();
                });
            }
        };
    }, []);
    useEffect(function onCommandBarChanged() {
        if (props.useSiteCommandBar)
            commandBar.setItems([
                ...props.commandBarItems,
                {
                    key: 'refresh',
                    iconProps: { iconName: 'Refresh' },
                    name: lang.toUpdate,
                    onClick: () => {
                        downloadServerDataAsync();
                    }
                }
            ]);
    }, [props.commandBarItems]);
    const downloadServerDataAsync = async () => {
        const req = Object.assign({}, request);
        req.length = request.length || 20;
        if (req.start < 0)
            req.start = 0;
        setIsLoading(true);
        let response;
        try {
            const r = new DataTableRequest(req);
            response = await props.onDownloadServerDataAsync(r);
            if (req.start < 0)
                req.start = 0;
            let lastItem = (Math.max(req.start, 1) * req.length);
            if (lastItem > response.recordsFiltered)
                lastItem = response.recordsFiltered;
            const totalPages = req.length > 0 ? Math.ceil(response.recordsFiltered / req.length) : 0;
            setResponseStatus({
                totalPages,
                lastItem,
                totalRecords: response.recordsFiltered,
                items: response.data || []
            });
        }
        catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    };
    const onSearchTextKeyUp = (event) => {
        if (!isLoading) {
            if (event.keyCode === 13) {
                setRequest(Object.assign(Object.assign({}, request), { search: search.value, start: 1 }));
            }
        }
    };
    let start = ((request.start - 1) * request.length) + 1;
    if (start < 0)
        start = 1;
    const renderTable = () => React.createElement(XDataTable, { columns: props.columns, compact: props.compact, items: resp.items, onGetKey: props.onGetKey, onSelectionChanged: props.onSelectionChanged, selectedItems: props.selectedItems || [], selectionMode: props.selectionMode, request: request, onDownloadServerData: async (request) => {
            downloadServerDataAsync();
        } });
    const DATATABLE_HEADER_STYLE = Object.assign({ alignItems: "center", backdropFilter: "blur(6px)", backgroundColor: theme.palette.white, borderRadius: "6px", boxShadow: "rgba(47, 43, 61, 0.14) 0px 2px 6px 0px", boxSizing: "border-box", color: "rgba(47, 43, 61, 0.78)", display: "flex", justifyContent: "space-between", lineHeight: "22px", minHeight: "44px", height: "44px", padding: "0px 5px 0px 10px", position: "relative", userSelect: "none", marginLeft: "5px", marginRight: "5px", paddingRight: 0, paddingLeft: 0 }, (_a = props.styles) === null || _a === void 0 ? void 0 : _a.header);
    const SEARCH_TEXTFIELD_STYLE = {
        height: "43px",
        backgroundColor: theme.palette.white,
        marginTop: "-5px",
        borderTopRightRadius: "4px",
        borderBottomRightRadius: "4px"
    };
    return React.createElement(Page, { title: props.title, onRenderHeader: props.useSiteCommandBar ? null : React.createElement(React.Fragment, null,
            React.createElement("div", { "data-role": "datatable-header", style: DATATABLE_HEADER_STYLE },
                React.createElement("div", { style: { width: '70%' } },
                    React.createElement(CommandBar, { items: [
                            ...(props.commandBarItems || []),
                            {
                                key: 'refresh',
                                iconProps: { iconName: 'Refresh' },
                                name: lang.toUpdate,
                                onClick: () => {
                                    downloadServerDataAsync();
                                }
                            }
                        ], styles: {
                            root: {
                                paddingLeft: 5,
                                marginLeft: 0,
                            }
                        }, farItems: commandBarFarItems })),
                React.createElement("div", { style: { width: '30%', background: '#fafafa' } },
                    React.createElement(XTextField, { placeholder: 'Buscar...', borderless: true, className: 'mp-search', styles: {
                            root: {
                                borderLeft: '1px solid #dbdbdb',
                            }
                        }, style: SEARCH_TEXTFIELD_STYLE, onChange: value => layout.setSearchBoxValue(value), value: search.value, onKeyUp: onSearchTextKeyUp })))), onRenderFooter: React.createElement(React.Fragment, null, resp.totalRecords > 0 && React.createElement(React.Fragment, null,
            isSidebarVisible && React.createElement("span", null, lang.__replace(lang.dataTablePaginationMessage, { start, lastItem: resp.lastItem, totalRecords: resp.totalRecords })),
            React.createElement(Paginator, { currentPage: request.start, totalPages: resp.totalPages, onChange: (page) => {
                    setRequest(Object.assign(Object.assign({}, request), { start: page }));
                } }))), styles: {
            root: Object.assign({}, (_d = (_c = (_b = props.styles) === null || _b === void 0 ? void 0 : _b.subComponents) === null || _c === void 0 ? void 0 : _c.page) === null || _d === void 0 ? void 0 : _d.root),
            header: Object.assign({ borderBottom: props.useSiteCommandBar ? 'solid 1px silver' : 'none' }, (_g = (_f = (_e = props.styles) === null || _e === void 0 ? void 0 : _e.subComponents) === null || _f === void 0 ? void 0 : _f.page) === null || _g === void 0 ? void 0 : _g.header),
            body: Object.assign({ overflowX: 'auto' }, (_k = (_j = (_h = props.styles) === null || _h === void 0 ? void 0 : _h.subComponents) === null || _j === void 0 ? void 0 : _j.page) === null || _k === void 0 ? void 0 : _k.body),
            title: Object.assign({ fontSize: '1.5em', fontWeight: 600, color: theme.palette.black }, (_l = props.styles) === null || _l === void 0 ? void 0 : _l.title),
            footer: Object.assign({ justifyContent: isSidebarVisible ? 'space-between' : 'flex-end' }, (_p = (_o = (_m = props.styles) === null || _m === void 0 ? void 0 : _m.subComponents) === null || _o === void 0 ? void 0 : _o.page) === null || _p === void 0 ? void 0 : _p.footer)
        } },
        resp.totalRecords > 0 && React.createElement(React.Fragment, null,
            props.showFolders && React.createElement("div", { "data-role": "datatable-page" },
                React.createElement("div", null,
                    React.createElement(XTreeView, { node: foldersNode, useIcons: true })),
                renderTable()),
            !props.showFolders && React.createElement(React.Fragment, null, renderTable())),
        resp.totalRecords === 0 && React.createElement(React.Fragment, null,
            React.createElement("div", { className: "io-xdatatable-empty" }, "No hay registros para mostrar.")));
};
